import React, { useEffect, useMemo, useState } from 'react'
import { Container, UploadProgress } from '../../../components'
import {
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Theme,
    TableContainer,
    IconButton,
    MenuItem,
    Select,
    FormControl,
    FormLabel,
    RadioGroup,
    Radio,
    FormControlLabel,
    CircularProgress,
    Checkbox,
    Button,
    Tooltip,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    TablePagination,
} from '@material-ui/core'

import MoreVertIcon from '@material-ui/icons/MoreVert'
import RefreshIcon from '@material-ui/icons/Refresh'
import PublishIcon from '@material-ui/icons/Publish'

import { LeaseAuditController } from '../../../hooks/documentAudit'
import { useUser } from '../../../hooks'
import { SearchField } from '../../../components/SearchField'
import { isAccountManager, toMMDDYYYY } from '../../../helpers'
import { useSelectionMap } from '../../../hooks/useSelectionMap'
import { LeaseDocumentManagerModal } from './LeaseDocumentManagerModal'
import { LeasePageDocumentDetail } from './LeasePageDocumentDetail'

interface Props {
    theme: Theme
    height: number
    leaseAuditController: LeaseAuditController
}

export const LeasePage = (props: Props) => {
    const { theme, height, leaseAuditController } = props

    const { workspaceUser } = useUser()
    const superUser = isAccountManager(workspaceUser)

    const [
        selectedDocOccuranceId,
        setSelectedDocOccuranceId,
    ] = useState<number>(-1)

    const [offset, setOffset] = useState(0)
    const [limit, setLimit] = useState(25)
    const [totalRows, setTotalRows] = useState(0)
    const [selectedDocumentFilter, setSelectedDocumentFilter] = useState<
        number | undefined
    >(undefined)
    const [selectedIntegrationId, setSelectedIntegrationId] = useState<
        string | undefined
    >(undefined)
    const selectionController = useSelectionMap()
    const [allHiddenSelected, setAllHiddenSelected] = useState(false)
    const [leaseDocumentManagerOpen, setLeaseDocumentManagerOpen] = useState(
        false,
    )
    const [pageCountDialogOpen, setPageCountDialogOpen] = useState(false)
    const [pendingSelectedIds, setPendingSelectedIds] = useState<number[]>([])
    const [extractDataDialogOpen, setExtractDataDialogOpen] = useState(false)
    const [pendingExtractIds, setPendingExtractIds] = useState<number[]>([])
    const [selectedModelProvider, setSelectedModelProvider] = useState<string>(
        'gemini',
    )
    const documentOccurances =
        leaseAuditController.leaseDocumentOccurances || []

    const leaseAudit = leaseAuditController.leaseAudit

    if (!leaseAudit) {
        return <span>No lease audit selected</span>
    }

    const dataSource = leaseAudit.data_source

    const loadDocumentOccurances = () => {
        leaseAuditController
            .fetchLeaseDocumentOccurances(
                leaseAudit.id,
                selectedDocumentFilter,
                selectedIntegrationId,
                offset,
                limit,
            )
            .then((response) => {
                if (response) {
                    setTotalRows(response.count)
                }
            })
    }

    useEffect(() => {
        loadDocumentOccurances()
    }, [])

    const displayStatus = (status: number) => {
        switch (status) {
            case 200:
                return 'Initial'
            case 201:
                return 'Upload Complete'
            case 401:
                return 'Upload Failed'
            case 204:
                return 'Extraction Complete'
            case 404:
                return 'Extraction Failed'
            default:
                return 'Unknown'
        }
    }

    const allSelected =
        selectionController.selectionMap.length === documentOccurances.length
    const nonSelected = selectionController.selectionMap.length === 0

    const intermediate = !allSelected && !nonSelected

    const handleGetPageCount = () => {
        const selectedIds: number[] = []
        Object.keys(selectionController.selectionMap).forEach((id) => {
            const numId = Number(id)
            if (isNaN(numId)) {
                return
            }

            if (selectionController.selectionMap[numId]) {
                selectedIds.push(numId)
            }
        })

        setPendingSelectedIds(selectedIds)
        setPageCountDialogOpen(true)
    }

    const handleConfirmPageCount = () => {
        leaseAuditController.getPageCount(leaseAudit.id, pendingSelectedIds)
        setPageCountDialogOpen(false)
    }

    const handleExtractData = () => {
        const selectedIds: number[] = []
        Object.keys(selectionController.selectionMap).forEach((id) => {
            const numId = Number(id)
            if (isNaN(numId)) {
                return
            }

            if (selectionController.selectionMap[numId]) {
                selectedIds.push(numId)
            }
        })

        setPendingExtractIds(selectedIds)
        setExtractDataDialogOpen(true)
    }

    const handleConfirmExtractData = () => {
        leaseAuditController.runVersion2(
            leaseAudit.id,
            pendingExtractIds,
            selectedModelProvider,
        )
        setExtractDataDialogOpen(false)
    }

    const handlePageChange = (event: unknown, newPage: number) => {
        setOffset(newPage * limit)
        setTimeout(() => {
            loadDocumentOccurances()
        }, 0)
    }

    const handleRowsPerPageChange = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const newLimit = parseInt(event.target.value, 10)
        setLimit(newLimit)
        setOffset(0)
        setTimeout(() => {
            loadDocumentOccurances()
        }, 0)
    }

    return (
        <Container
            style={{
                flex: 1,
                flexDirection: 'column',
                padding: theme.spacing(2),
            }}
        >
            {/* Header */}
            <Container
                style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    flex: 1,
                    maxHeight: 50,
                }}
            >
                <IconButton
                    style={{
                        marginLeft: theme.spacing(1),
                    }}
                    onClick={() => {
                        loadDocumentOccurances()
                        setOffset(0)
                        setLimit(25)
                    }}
                >
                    <RefreshIcon fontSize="large" />
                </IconButton>

                {/* status filter */}
                <FormControl style={{ marginLeft: theme.spacing(4) }}>
                    <FormLabel id="lease-document-pipeline-filter-label">
                        Status Filter
                    </FormLabel>
                    <RadioGroup
                        row
                        aria-labelledby="lease-document-pipeline-filter-label"
                        name="lease-document-pipeline-filter"
                        value={selectedDocumentFilter?.toString() ?? ''}
                        onChange={(e) => {
                            const value = e.target.value
                            setSelectedDocumentFilter(
                                value === '' ? undefined : Number(value),
                            )
                        }}
                    >
                        <FormControlLabel
                            value=""
                            control={<Radio />}
                            label="All"
                        />
                        <FormControlLabel
                            value={INITIAL.toString()}
                            control={<Radio />}
                            label="Initial"
                        />
                        <FormControlLabel
                            value={EXTRACTION_COMPLETE.toString()}
                            control={<Radio />}
                            label="Extraction Complete"
                        />
                        <FormControlLabel
                            value={EXTRACTION_FAILED.toString()}
                            control={<Radio />}
                            label="Extraction Failed"
                        />
                    </RadioGroup>
                </FormControl>

                {/* Add document title filter for INTEGRATED source */}
                {dataSource === 'INTEGRATED' && (
                    <FormControl style={{ marginLeft: theme.spacing(4) }}>
                        <FormLabel id="integration-id-filter-label">
                            Integration ID
                        </FormLabel>
                        <SearchField
                            value={selectedIntegrationId || ''}
                            onChange={(e) => {
                                const value = e.target.value
                                setSelectedIntegrationId(value || undefined)
                            }}
                            placeholder="Enter Integration ID"
                            style={{ minWidth: 200 }}
                        />
                    </FormControl>
                )}
                <Container
                    style={{
                        justifyContent: 'flex-end',
                        flex: 1,
                    }}
                >
                    {/* Buttons */}
                    <Button
                        variant="contained"
                        color="primary"
                        style={{
                            textTransform: 'none',
                            marginLeft: theme.spacing(4),
                        }}
                        onClick={handleGetPageCount}
                    >
                        Get Page Count
                    </Button>

                    <Button
                        variant="contained"
                        color="primary"
                        style={{
                            marginLeft: theme.spacing(2),
                            textTransform: 'none',
                        }}
                        onClick={handleExtractData}
                    >
                        Extract Data
                    </Button>
                </Container>
            </Container>

            {/* uploaded table */}

            {dataSource === 'UPLOADED' && (
                <TableContainer
                    style={{
                        height: height - 50,
                        flexDirection: 'column',
                        overflow: 'auto',
                        marginTop: theme.spacing(0),
                    }}
                >
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <Checkbox
                                        checked={allSelected}
                                        indeterminate={intermediate}
                                        onChange={() => {
                                            setAllHiddenSelected(false)
                                            if (nonSelected) {
                                                selectionController.selectAll(
                                                    documentOccurances,
                                                )
                                            } else {
                                                selectionController.clearSelections()
                                            }
                                        }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <span>Status</span>
                                </TableCell>
                                <TableCell>
                                    <span>Created Date</span>
                                </TableCell>
                                <TableCell>
                                    <span>Batch</span>
                                </TableCell>

                                <TableCell>
                                    <span>Resident</span>
                                </TableCell>
                                <TableCell>
                                    <span>Lease Start</span>
                                </TableCell>
                                <TableCell>
                                    <span>Lease End</span>
                                </TableCell>
                                <TableCell>
                                    <span>File Name</span>
                                </TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {leaseAuditController.loading
                                .fetchDocumentOccurances ? (
                                <TableRow>
                                    <TableCell colSpan={5}>
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : (
                                documentOccurances?.map((docOccurance) => {
                                    const document = docOccurance.document
                                    const createdDate = new Date(
                                        document.created_date,
                                    )
                                    const resident =
                                        document.entity_values.find(
                                            (ev) =>
                                                ev.entity_alias.alias ===
                                                'resident',
                                        )?.value || 'N/A'

                                    const leaseStart =
                                        document.entity_values.find(
                                            (ev) =>
                                                ev.entity_alias.alias ===
                                                'lease_start',
                                        )?.value || 'N/A'
                                    const leaseEnd =
                                        document.entity_values.find(
                                            (ev) =>
                                                ev.entity_alias.alias ===
                                                'lease_end',
                                        )?.value || 'N/A'

                                    return (
                                        <TableRow key={docOccurance.id}>
                                            <TableCell>
                                                <Checkbox
                                                    checked={selectionController.isIdSelected(
                                                        docOccurance.id,
                                                    )}
                                                    onChange={(e) => {
                                                        selectionController.setIdSelectionValue(
                                                            docOccurance.id,
                                                            e.target.checked,
                                                        )
                                                        setAllHiddenSelected(
                                                            false,
                                                        )
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {displayStatus(
                                                    Number(
                                                        docOccurance.document
                                                            .status,
                                                    ),
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {toMMDDYYYY(createdDate)}
                                            </TableCell>
                                            <TableCell>
                                                {docOccurance.document_batch}
                                            </TableCell>

                                            <TableCell>
                                                <span>{resident}</span>
                                            </TableCell>
                                            <TableCell>{leaseStart}</TableCell>
                                            <TableCell>{leaseEnd}</TableCell>

                                            <TableCell>
                                                {document.file_name}
                                            </TableCell>

                                            <TableCell>
                                                <IconButton
                                                    onClick={() => {
                                                        setSelectedDocOccuranceId(
                                                            docOccurance.id,
                                                        )
                                                    }}
                                                >
                                                    <MoreVertIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    )
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Integrated Table */}

            {dataSource === 'INTEGRATED' && (
                <TableContainer
                    component={Paper}
                    style={{
                        height: height - 50,
                        flexDirection: 'column',
                        overflow: 'auto',
                        marginTop: theme.spacing(0),
                    }}
                >
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <Checkbox
                                        checked={allSelected}
                                        indeterminate={intermediate}
                                        onChange={() => {
                                            setAllHiddenSelected(false)
                                            if (nonSelected) {
                                                selectionController.selectAll(
                                                    documentOccurances,
                                                )
                                            } else {
                                                selectionController.clearSelections()
                                            }
                                        }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <span>Status</span>
                                </TableCell>
                                <TableCell>
                                    <span>Created Date</span>
                                </TableCell>
                                <TableCell>
                                    <span>Document Type</span>
                                </TableCell>
                                <TableCell>
                                    <span>Integration ID</span>
                                </TableCell>
                                <TableCell>
                                    <span>Entrata Document Title</span>
                                </TableCell>

                                <TableCell>
                                    <span>Resident</span>
                                </TableCell>
                                <TableCell>
                                    <span>Lease Start</span>
                                </TableCell>
                                <TableCell>
                                    <span>Lease End</span>
                                </TableCell>
                                <TableCell>
                                    <span>File Name</span>
                                </TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {leaseAuditController.loading
                                .fetchDocumentOccurances ? (
                                <TableRow>
                                    <TableCell colSpan={5}>
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : (
                                documentOccurances?.map((docOccurance) => {
                                    const document = docOccurance.document
                                    const createdDate = new Date(
                                        document.created_date,
                                    )
                                    const resident =
                                        document.entity_values.find(
                                            (ev) =>
                                                ev.entity_alias.alias ===
                                                'resident',
                                        )?.value || 'N/A'
                                    const leaseStart =
                                        document.entity_values.find(
                                            (ev) =>
                                                ev.entity_alias.alias ===
                                                'lease_start',
                                        )?.value || 'N/A'
                                    const leaseEnd =
                                        document.entity_values.find(
                                            (ev) =>
                                                ev.entity_alias.alias ===
                                                'lease_end',
                                        )?.value || 'N/A'

                                    return (
                                        <TableRow key={docOccurance.id}>
                                            <TableCell>
                                                <Checkbox
                                                    checked={selectionController.isIdSelected(
                                                        docOccurance.id,
                                                    )}
                                                    onChange={(e) => {
                                                        selectionController.setIdSelectionValue(
                                                            docOccurance.id,
                                                            e.target.checked,
                                                        )
                                                        setAllHiddenSelected(
                                                            false,
                                                        )
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {displayStatus(
                                                    Number(
                                                        docOccurance.document
                                                            .status,
                                                    ),
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {toMMDDYYYY(createdDate)}
                                            </TableCell>
                                            <TableCell>
                                                {document.entrata_document_type}
                                            </TableCell>
                                            <TableCell>
                                                {document.integration_id}
                                            </TableCell>

                                            <TableCell>
                                                {
                                                    document.entrata_document_title
                                                }
                                            </TableCell>

                                            <TableCell>
                                                <span>{resident}</span>
                                            </TableCell>
                                            <TableCell>{leaseStart}</TableCell>
                                            <TableCell>{leaseEnd}</TableCell>
                                            <TableCell>
                                                {document.file_name}
                                            </TableCell>

                                            <TableCell>
                                                <IconButton
                                                    onClick={() => {
                                                        setSelectedDocOccuranceId(
                                                            docOccurance.id,
                                                        )
                                                    }}
                                                >
                                                    <MoreVertIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    )
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* footer */}
            <Container
                style={{
                    marginTop: theme.spacing(2),
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: theme.spacing(2),
                    flexDirection: 'row',
                }}
            >
                <TablePagination
                    component="div"
                    rowsPerPageOptions={[25, 50, 100]}
                    count={totalRows}
                    rowsPerPage={limit}
                    page={offset / limit}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handleRowsPerPageChange}
                />

                {superUser && (
                    <Tooltip title="Manage Rent Roll Documents">
                        <IconButton
                            size="small"
                            onClick={() => {
                                setLeaseDocumentManagerOpen(true)
                            }}
                        >
                            <PublishIcon />
                        </IconButton>
                    </Tooltip>
                )}
            </Container>

            <UploadProgress
                theme={theme}
                uploadBatch={leaseAuditController.uploadBatch}
                currentFileIndex={leaseAuditController.currentFileIndex}
            />

            <LeaseDocumentManagerModal
                open={leaseDocumentManagerOpen}
                onClose={() => setLeaseDocumentManagerOpen(false)}
                leaseAuditController={leaseAuditController}
            />

            <LeasePageDocumentDetail
                selectedDocOccuranceId={selectedDocOccuranceId}
                onClose={() => setSelectedDocOccuranceId(-1)}
                theme={theme}
                leaseAuditController={leaseAuditController}
            />

            <Dialog
                open={pageCountDialogOpen}
                onClose={() => setPageCountDialogOpen(false)}
            >
                <DialogTitle>Confirm Page Count</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {pendingSelectedIds.length === 0
                            ? `No documents selected. This will process all ${totalRows} documents.`
                            : `Process page count for ${
                                  pendingSelectedIds.length
                              } selected document${
                                  pendingSelectedIds.length === 1 ? '' : 's'
                              }?`}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setPageCountDialogOpen(false)}
                        color="primary"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmPageCount}
                        color="primary"
                        autoFocus
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={extractDataDialogOpen}
                onClose={() => setExtractDataDialogOpen(false)}
            >
                <DialogTitle>Confirm Data Extraction</DialogTitle>
                <DialogContent>
                    {/* model selector */}
                    <Container style={{ marginBottom: theme.spacing(2) }}>
                        <FormControl>
                            <FormLabel>Model Provider</FormLabel>
                            <Select
                                value={selectedModelProvider}
                                onChange={(e) => {
                                    setSelectedModelProvider(
                                        e.target.value as string,
                                    )
                                }}
                            >
                                <MenuItem value="gemini">Gemini</MenuItem>
                                <MenuItem value="openai">Open AI</MenuItem>
                            </Select>
                        </FormControl>
                    </Container>
                    <DialogContentText>
                        {pendingExtractIds.length === 0
                            ? `No documents selected. This will extract data from all ${totalRows} documents.`
                            : `Extract data from ${
                                  pendingExtractIds.length
                              } selected document${
                                  pendingExtractIds.length === 1 ? '' : 's'
                              }?`}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setExtractDataDialogOpen(false)}
                        color="primary"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmExtractData}
                        color="primary"
                        autoFocus
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    )
}

const INITIAL = 200
const UPLOAD_COMPLETE = 201
const UPLOAD_FAILED = 401
const EXTRACTION_COMPLETE = 204
const EXTRACTION_FAILED = 404
